import axios from "axios";
import { buildApiUrl } from "./ApiConfig";

interface LoginResponse {
  token: string;
  expiresAt: number;
}

interface ScrapingJob {
  id?: number;
  jobType: string;
  status: string;
  targetIds?: number[];
  totalItems: number;
  processedItems: number;
  successCount: number;
  errorCount: number;
  errorMessages?: string[];
  startedAt?: number;
  completedAt?: number;
  createdAt: number;
  progress: number;
  currentOperation?: string;
  lastUpdate?: number;
  canCancel?: boolean;
  canSync?: boolean;
  syncedAt?: number;
  backupFilename?: string;
}

interface RestaurantUpdateRequest {
  name?: string;
  address?: string;
  latitude?: number;
  longitude?: number;
  operatorNumber?: string;
}

interface SystemLog {
  id?: number;
  action: string;
  details?: string;
  createdAt: number;
}

interface BackupInfo {
  filename: string;
  dbType: 'IMMUTABLE' | 'MUTABLE';
  createdAt: string;
  sizeBytes: number;
  path: string;
}

interface Subscription {
  id?: number;
  restaurantId: number;
  email: string;
  createdAt: number;
  verified: boolean;
  verificationToken?: string | null;
  lastNotified?: number | null;
  unsubscribeToken: string;
}

interface SubscriptionWithRestaurant {
  subscription: Subscription;
  restaurantName: string;
  restaurantAddress?: string;
}

interface SubscriptionsResponse {
  subscriptions: SubscriptionWithRestaurant[];
  total: number;
  page: number;
  pageSize: number;
}

interface SubscriptionStats {
  totalSubscriptions: number;
  verifiedSubscriptions: number;
  unverifiedSubscriptions: number;
  uniqueRestaurants: number;
  recentSubscriptions: number;
  topRestaurants: Array<{
    restaurantId: number;
    restaurantName: string;
    subscriptionCount: number;
  }>;
}

interface Report {
  id?: number;
  restaurantId: number;
  username: string;
  reportText: string;
  rating: number;
  imageUrl?: string;
  createdAt: number;
  status?: string;
}

interface ReportWithRestaurant {
  report: Report;
  restaurantName: string;
  restaurantAddress?: string;
}

interface ReportsResponse {
  reports: ReportWithRestaurant[];
  total: number;
  page: number;
  pageSize: number;
}

interface ReportUpdateRequest {
  reportText?: string;
  username?: string;
  rating?: number;
  status?: string;
}

class AdminApiService {
  private token: string | null = null;

  constructor() {
    if (typeof window === 'undefined') return;
    // Load token from localStorage on init
    const savedToken = localStorage.getItem('adminToken');
    const expiresAt = localStorage.getItem('adminTokenExpires');

    if (savedToken && expiresAt && parseInt(expiresAt) > Date.now()) {
      this.token = savedToken;
    } else {
      // Clear expired token
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminTokenExpires');
    }
  }

  private getAuthHeaders() {
    if (!this.token) {
      throw new Error('Not authenticated');
    }
    return {
      'Authorization': `Bearer ${this.token}`,
      'Content-Type': 'application/json'
    };
  }

  async login(password: string): Promise<boolean> {
    try {
      const response = await axios.post<LoginResponse>(
        buildApiUrl('api/admin/login'),
        { password }
      );
      
      this.token = response.data.token;
      localStorage.setItem('adminToken', response.data.token);
      localStorage.setItem('adminTokenExpires', response.data.expiresAt.toString());
      
      return true;
    } catch (error) {
      console.error('Login failed:', error);
      return false;
    }
  }

  logout() {
    this.token = null;
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminTokenExpires');
  }

  isAuthenticated(): boolean {
    if (typeof window === 'undefined') return false;
    const expiresAt = localStorage.getItem('adminTokenExpires');
    return !!this.token && !!expiresAt && parseInt(expiresAt) > Date.now();
  }

  async getStats(): Promise<any> {
    const response = await axios.get(
      buildApiUrl('api/admin/stats'),
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async searchRestaurants(query: string, limit = 50): Promise<any[]> {
    const response = await axios.get(
      buildApiUrl(`api/admin/restaurants/search?q=${encodeURIComponent(query)}&limit=${limit}`),
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async getRestaurant(id: number): Promise<any> {
    const response = await axios.get(
      buildApiUrl(`api/admin/restaurants/${id}`),
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async updateRestaurant(id: number, updates: RestaurantUpdateRequest): Promise<boolean> {
    try {
      await axios.put(
        buildApiUrl(`api/admin/restaurants/${id}`),
        updates,
        { headers: this.getAuthHeaders() }
      );
      return true;
    } catch (error) {
      console.error('Failed to update restaurant:', error);
      return false;
    }
  }

  async createScrapingJob(jobType: string, targetIds?: number[]): Promise<ScrapingJob> {
    const response = await axios.post<ScrapingJob>(
      buildApiUrl('api/admin/scraping/jobs'),
      { jobType, targetIds },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async getScrapingJobs(status?: string): Promise<ScrapingJob[]> {
    const url = status 
      ? buildApiUrl(`api/admin/scraping/jobs?status=${status}`)
      : buildApiUrl('api/admin/scraping/jobs');
    
    const response = await axios.get<ScrapingJob[]>(
      url,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async getScrapingJob(id: number): Promise<ScrapingJob> {
    const response = await axios.get<ScrapingJob>(
      buildApiUrl(`api/admin/scraping/jobs/${id}`),
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async getActiveScrapingJobs(): Promise<ScrapingJob[]> {
    const response = await axios.get<ScrapingJob[]>(
      buildApiUrl('api/admin/scraping/active'),
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async cancelScrapingJob(id: number): Promise<boolean> {
    try {
      await axios.post(
        buildApiUrl(`api/admin/scraping/jobs/${id}/cancel`),
        {},
        { headers: this.getAuthHeaders() }
      );
      return true;
    } catch (error) {
      console.error('Failed to cancel scraping job:', error);
      return false;
    }
  }

  async syncScrapingJob(id: number): Promise<boolean> {
    try {
      await axios.post(
        buildApiUrl(`api/admin/scraping/jobs/${id}/sync`),
        {},
        { headers: this.getAuthHeaders() }
      );
      return true;
    } catch (error) {
      console.error('Failed to sync scraping job:', error);
      return false;
    }
  }

  async rollbackScraping(backupFilename: string): Promise<boolean> {
    try {
      await axios.post(
        buildApiUrl(`api/admin/scraping/rollback/${encodeURIComponent(backupFilename)}`),
        {},
        { headers: this.getAuthHeaders() }
      );
      return true;
    } catch (error) {
      console.error('Failed to rollback scraping:', error);
      return false;
    }
  }

  async getLogs(limit = 100): Promise<SystemLog[]> {
    const response = await axios.get<SystemLog[]>(
      buildApiUrl(`api/admin/logs?limit=${limit}`),
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  // Backup management
  async getBackups(): Promise<BackupInfo[]> {
    const response = await axios.get<BackupInfo[]>(
      buildApiUrl('api/admin/backup'),
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async createBackup(dbType: 'IMMUTABLE' | 'MUTABLE'): Promise<BackupInfo> {
    const response = await axios.post<BackupInfo>(
      buildApiUrl('api/admin/backup'),
      { dbType },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async restoreBackup(filename: string): Promise<boolean> {
    try {
      await axios.post(
        buildApiUrl('api/admin/backup/restore'),
        { filename },
        { headers: this.getAuthHeaders() }
      );
      return true;
    } catch (error) {
      console.error('Failed to restore backup:', error);
      return false;
    }
  }

  async deleteBackup(filename: string): Promise<boolean> {
    try {
      await axios.delete(
        buildApiUrl(`api/admin/backup/${encodeURIComponent(filename)}`),
        { headers: this.getAuthHeaders() }
      );
      return true;
    } catch (error) {
      console.error('Failed to delete backup:', error);
      return false;
    }
  }

  // Subscription management
  async getSubscriptions(page = 1, pageSize = 50, verifiedOnly = false, search?: string): Promise<SubscriptionsResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      verifiedOnly: verifiedOnly.toString()
    });
    
    if (search) {
      params.append('search', search);
    }
    
    const response = await axios.get<SubscriptionsResponse>(
      buildApiUrl(`api/admin/subscriptions?${params}`),
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async getSubscriptionStats(): Promise<SubscriptionStats> {
    const response = await axios.get<SubscriptionStats>(
      buildApiUrl('api/admin/subscriptions/stats'),
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async deleteSubscription(id: number): Promise<boolean> {
    try {
      await axios.delete(
        buildApiUrl(`api/admin/subscriptions/${id}`),
        { headers: this.getAuthHeaders() }
      );
      return true;
    } catch (error) {
      console.error('Failed to delete subscription:', error);
      return false;
    }
  }

  async verifySubscription(id: number): Promise<boolean> {
    try {
      await axios.post(
        buildApiUrl(`api/admin/subscriptions/${id}/verify`),
        {},
        { headers: this.getAuthHeaders() }
      );
      return true;
    } catch (error) {
      console.error('Failed to verify subscription:', error);
      return false;
    }
  }

  async exportSubscriptions(verifiedOnly = false): Promise<void> {
    const params = new URLSearchParams({
      format: 'csv',
      verifiedOnly: verifiedOnly.toString()
    });
    
    const response = await axios.get(
      buildApiUrl(`api/admin/subscriptions/export?${params}`),
      { 
        headers: this.getAuthHeaders(),
        responseType: 'blob'
      }
    );
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'subscriptions.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  // Report management
  async getReports(page = 1, pageSize = 50, status?: string): Promise<ReportsResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString()
    });
    if (status) {
      params.append('status', status);
    }
    const response = await axios.get<ReportsResponse>(
      buildApiUrl(`api/admin/reports?${params}`),
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async getReport(id: number): Promise<Report> {
    const response = await axios.get<Report>(
      buildApiUrl(`api/admin/reports/${id}`),
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async updateReport(id: number, updates: ReportUpdateRequest): Promise<boolean> {
    try {
      await axios.put(
        buildApiUrl(`api/admin/reports/${id}`),
        updates,
        { headers: this.getAuthHeaders() }
      );
      return true;
    } catch (error) {
      console.error('Failed to update report:', error);
      return false;
    }
  }

  async moderateReport(id: number, status: string): Promise<boolean> {
    return this.updateReport(id, { status });
  }

  async deleteReport(id: number): Promise<boolean> {
    try {
      await axios.delete(
        buildApiUrl(`api/admin/reports/${id}`),
        { headers: this.getAuthHeaders() }
      );
      return true;
    } catch (error) {
      console.error('Failed to delete report:', error);
      return false;
    }
  }

  async searchReports(query: string): Promise<Report[]> {
    const response = await axios.get<Report[]>(
      buildApiUrl(`api/admin/reports/search?q=${encodeURIComponent(query)}`),
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }
}

export default new AdminApiService();